'use client'

import { useMemo, useState, useEffect } from 'react'
import {
  X,
  Save,
  Package,
  DollarSign,
  ImageIcon,
  CalendarDays,
  LockKeyhole,
  Settings2,
  Handshake,
  ChevronDown,
  PartyPopper,
  Tag,
  Video,
  FileDown,
  Repeat,
  MapPin,
  Layers,
  Eye,
  Trash2,
  Plus,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { RichTextEditor } from '@/components/funnel/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CommunitySubscriptionsSelector } from '@/components/settings/CommunitySubscriptionsSelector'
import { cn } from '@/lib/utils'
import { ImageManager } from '@/components/image/ImageManager'
import {
  TrackableVideoEmbed,
} from '@/components/media/TrackableVideoEmbed'
import { VideoUrlModal } from '@/components/editor/VideoUrlModal'

export type ProductType =
  | 'course'
  | 'challenge'
  | 'event'
  | 'digital'
  | 'service'
  | 'physical'
  | 'membership'
  | 'bundle'
  | 'custom'

export type PricingType =
  | 'one_time'
  | 'subscription'
  | 'free'
  | 'custom'

type DrawerSectionId =
  | 'main'
  | 'pricing'
  | 'recurring'
  | 'promo'
  | 'media'
  | 'event'
  | 'availability'
  | 'access'
  | 'upsells'
  | 'advanced'
  | 'affiliates'
  | 'thankyou'

export type OfferPresetId = ProductType

export type ThankYouAction =
  | 'home'
  | 'onboarding'
  | 'moments'
  | 'community'
  | 'order'
  | 'membership'
  | 'link'

export type ProductForm = {
  productId: number | null
  type: OfferPresetId

  productType: ProductType
  pricingType: PricingType

  name: string
  description: string
  metaDescription: string
  metaTitle: string
  metaKeyword: string
  tag: string
  keyword: string
  model: string

  image: string
  video: string
  gallery: Array<{ image: string; sortOrder: number }>

  price: string
  quantity: string
  minimum: string
  currency: string
  status: boolean
  isPublished: boolean
  installments: boolean

  recurringIds: number[]
  recurringEnabled: boolean
  recurringInterval: 'day' | 'week' | 'month' | 'year'
  recurringIntervalCount: string
  trialEnabled: boolean
  trialDays: string

  mtaSchemeId: number | null
  isMembership: boolean
  membershipExpiresIn: string
  membershipExpiresType: 'day' | 'week' | 'month' | 'year'

  promoEnabled: boolean
  promoType: 'date' | 'days'
  promoPrice: string
  promoDateStart: string
  promoDateEnd: string
  promoDays: string

  duration: 'limited' | 'forever'
  dateAvailable: string
  dateStart: string
  timeStart: string
  dateEnd: string
  timeEnd: string
  location: string

  expiresEnabled: boolean
  expiresIn: string
  expiresType: 'day' | 'week' | 'month' | 'year'

  accessCourseIds: number[]
  accessChallengeIds: number[]
  accessEventIds: number[]
  accessDownloadIds: number[]
  accessProductIds: number[]

  canClone: boolean
  canPromote: boolean
  sortOrder: string

  upsells: Array<{
    productId: number
    name: string
    price?: string
    status: boolean
    sortOrder: number
  }>

  thankyou: {
    title: string
    message: string
    video: string
    action: ThankYouAction
    link: string
    button: string
    status: boolean
  }
}

type Props = {
  open: boolean
  mode: 'create' | 'edit'
  form: ProductForm
  setForm: React.Dispatch<React.SetStateAction<ProductForm>>
  loading?: boolean
  saving?: boolean
  onClose: () => void
  onSave: () => void
  sellerId: number
  affiliatesEnabled?: boolean
  affiliateSchemes?: Array<{
    mta_scheme_id: number
    name: string
  }>
}

const typeLabels: Record<OfferPresetId, string> = {
  course: 'Curso',
  challenge: 'Reto / desafío',
  event: 'Evento',
  digital: 'Producto digital',
  service: 'Servicio',
  physical: 'Producto físico',
  membership: 'Membresía',
  bundle: 'Bundle / paquete',
  custom: 'Oferta personalizada',
}

function hasType(form: ProductForm, types: OfferPresetId[]) {
  return types.includes(form.type)
}

function updateField<K extends keyof ProductForm>(
  setForm: React.Dispatch<React.SetStateAction<ProductForm>>,
  key: K,
  value: ProductForm[K]
) {
  setForm((prev) => ({ ...prev, [key]: value }))
}

function updateThankyouField<K extends keyof ProductForm['thankyou']>(
  setForm: React.Dispatch<React.SetStateAction<ProductForm>>,
  key: K,
  value: ProductForm['thankyou'][K]
) {
  setForm((prev) => ({
    ...prev,
    thankyou: {
      ...prev.thankyou,
      [key]: value,
    },
  }))
}

function NumberIdListEditor({
  label,
  description,
  value,
  onChange,
  placeholder,
}: {
  label: string
  description: string
  value: number[]
  onChange: (ids: number[]) => void
  placeholder: string
}) {
  const rawValue = value.join(', ')

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        value={rawValue}
        placeholder={placeholder}
        onChange={(e) => {
          const ids = e.target.value
            .split(',')
            .map((v) => Number(String(v).trim()))
            .filter((n) => Number.isFinite(n) && n > 0)

          onChange(ids)
        }}
      />
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  )
}

function Section({
  id,
  icon: Icon,
  title,
  description,
  open,
  onToggle,
  children,
}: {
  id: DrawerSectionId
  icon: any
  title: string
  description?: string
  open: boolean
  onToggle: (id: DrawerSectionId) => void
  children: React.ReactNode
}) {
  return (
    <section className="border-b border-black/5 bg-white dark:border-white/10 dark:bg-[#0f172a]">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl border border-black/5 bg-[#f6f7fb] text-slate-700 shadow-sm dark:border-white/10 dark:bg-[#111b31] dark:text-slate-200">
            <Icon className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <div className="font-semibold text-foreground">{title}</div>
            {description ? (
              <div className="mt-1 text-sm leading-snug text-muted-foreground">
                {description}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-black/5 bg-white shadow-sm transition-all duration-300 dark:border-white/10 dark:bg-[#101826] ${
            open ? 'rotate-180 scale-105' : 'rotate-0'
          }`}
        >
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div
            className={`px-6 pb-6 transition-all duration-300 ${
              open ? 'translate-y-0 scale-[1] animate-in fade-in-50 slide-in-from-top-1' : '-translate-y-1 scale-[0.99]'
            }`}
          >
            <div className="rounded-2xl border border-black/5 bg-[#fbfbfc] p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-white/10 dark:bg-[#0b1324]">
              <div className="space-y-4">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function ProductDrawer({
  open,
  mode,
  form,
  setForm,
  loading,
  saving,
  onClose,
  onSave,
  sellerId,
  affiliatesEnabled = false,
  affiliateSchemes = [],
}: Props) {

  const [openSection, setOpenSection] = useState<DrawerSectionId | null>('main')
  const [imageManagerOpen, setImageManagerOpen] = useState(false)
  const [galleryReplaceIndex, setGalleryReplaceIndex] = useState<number | null>(null)
  const [imageTarget, setImageTarget] = useState<'main' | 'gallery'>('main')
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  const fieldClassName =
    'h-11 rounded-xl border border-black/10 bg-white px-3 text-sm shadow-sm transition-all outline-none ring-0 placeholder:text-muted-foreground/70 focus:border-primary/40 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.08)] dark:border-white/10 dark:bg-[#0f172a] dark:focus:bg-[#111c31]'

  const textareaClassName =
    'min-h-28 w-full rounded-xl border border-black/10 bg-white px-3 py-3 text-sm shadow-sm transition-all outline-none ring-0 placeholder:text-muted-foreground/70 focus:border-primary/40 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.08)] dark:border-white/10 dark:bg-[#0f172a] dark:focus:bg-[#111c31]'

  const selectClassName =
    'h-11 w-full rounded-xl border border-black/10 bg-white px-3 text-sm shadow-sm transition-all outline-none ring-0 focus:border-primary/40 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.08)] dark:border-white/10 dark:bg-[#0f172a] dark:focus:bg-[#111c31]'

  function toggleSection(id: DrawerSectionId) {
    setOpenSection((prev) => (prev === id ? null : id))
  }

  function openMainImageManager() {
    setImageTarget('main')
    setGalleryReplaceIndex(null)
    setImageManagerOpen(true)
  }

  function openGalleryImageManager(index: number | null = null) {
    setImageTarget('gallery')
    setGalleryReplaceIndex(index)
    setImageManagerOpen(true)
  }

  function handleSelectImage(img: { url: string }) {
    if (imageTarget === 'main') {
      updateField(setForm, 'image', img.url)
    } else {
      const next = [...form.gallery]

      if (galleryReplaceIndex !== null) {
        next[galleryReplaceIndex] = {
          ...next[galleryReplaceIndex],
          image: img.url,
        }
      } else {
        next.push({
          image: img.url,
          sortOrder: form.gallery.length + 1,
        })
      }

      updateField(setForm, 'gallery', next)
    }

    setImageManagerOpen(false)
    setGalleryReplaceIndex(null)
  }

  const visibility = useMemo(() => {
    const isSubscription = form.type === 'membership'
    const isEvent = form.type === 'event'
    const isDigital = form.type === 'digital'
    const isCustom = form.type === 'custom'

    return {
      showDescription: true,
      showMedia: true,
      showGallery: !hasType(form, ['membership']),
      showPricing: true,
      showQuantity: !isSubscription,
      showRecurring: isSubscription,
      showPromo: !isSubscription || isCustom,
      showAvailability: true,
      showEventSchedule: isEvent || isCustom,
      showDownloads: isDigital || isCustom,
      showAccess: false,
      showExpiration: !isEvent || isCustom,
      showAdvanced: true,
      showUpsells: false,
      showSeo: true,
    }
  }, [form])

  useEffect(() => {
    if (open) setOpenSection('main')
  }, [open])

  function updateRecurringIds(ids: number[]) {
    const cleaned = Array.from(
        new Set(
        (Array.isArray(ids) ? ids : [])
            .map((x) => Number(x))
            .filter((x) => Number.isFinite(x) && x > 0)
        )
    )

    updateField(setForm, 'recurringIds', cleaned)
  }

  return (
    <>
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>      
      <SheetContent
        side="right"
        className="flex h-dvh w-full max-w-none flex-col gap-0 border-l border-border bg-[#fcfcfd] p-0 shadow-2xl sm:w-[860px] sm:max-w-[860px] dark:bg-[#0b1020] [&>button]:right-4 [&>button]:top-4 [&>button]:rounded-full [&>button]:border [&>button]:border-border [&>button]:bg-background [&>button]:p-1 [&>button]:shadow-sm"
      >
        <SheetHeader className="shrink-0 border-b border-border bg-background/95 px-6 py-5 text-left backdrop-blur">
          <div className="min-w-0 pr-10">
            <SheetTitle className="text-left text-xl font-semibold tracking-tight">
              <span className="block truncate">
                {mode === 'create'
                  ? 'Nuevo producto'
                  : `Editar ${form.name?.trim() || 'producto'}`}
              </span>
            </SheetTitle>

            <SheetDescription className="mt-1 text-left text-sm text-muted-foreground">
              Configura tu oferta, contenido, promoción, accesos y monetización.
            </SheetDescription>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-sm text-muted-foreground">Cargando producto...</div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto bg-[#f7f8fa] dark:bg-[#0a0f1d]">
            <div className="pb-28">
              <Section
                id="main"
                icon={Package}
                title="Información principal"
                description="Elige el tipo de oferta y agrega nombre y descripción."
                open={openSection === 'main'}
                onToggle={toggleSection}
                >
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Tipo de oferta</Label>
                    <select className={selectClassName}
                      value={form.type}
                      onChange={(e) =>
                        updateField(setForm, 'type', e.target.value as OfferPresetId)
                      }
                    >
                      <option value="custom">Pago único</option>
                      <option value="membership">Membresía / suscripción</option>
                      <option className="hidden" value="event">Evento de pago</option>
                      <option className="hidden" value="digital">Producto digital</option>
                      <option className="hidden" value="custom">Oferta personalizada</option>
                    </select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>Nombre</Label>
                    <Input className={fieldClassName}
                      value={form.name}
                      onChange={(e) => updateField(setForm, 'name', e.target.value)}
                      placeholder="Ej: Acceso Premium + Comunidad"
                    />
                  </div>

                  {visibility.showDescription ? (
                    <>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Descripción completa</Label>

                        <RichTextEditor
                          content={form.description}
                          onChange={(value) =>
                            updateField(setForm, 'description', value)
                          }
                          placeholder="Explica qué incluye esta oferta."
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label>Descripción corta</Label>
                        <textarea className={textareaClassName}
                          value={form.metaDescription}
                          onChange={(e) =>
                            updateField(setForm, 'metaDescription', e.target.value)
                          }
                          placeholder="Texto corto para tarjetas, previews y SEO."
                        />
                      </div>
                    </>
                  ) : null}

                  <div className="space-y-2 hidden">
                    <Label>Modelo / SKU interno</Label>
                    <Input className={fieldClassName}
                      value={form.model}
                      onChange={(e) => updateField(setForm, 'model', e.target.value)}
                      placeholder="Ej: P-BM-ABC123"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Slug / keyword</Label>
                    <Input className={fieldClassName}
                      value={form.keyword}
                      onChange={(e) =>
                        updateField(
                          setForm,
                          'keyword',
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, '-')
                            .replace(/-+/g, '-')
                        )
                      }
                      placeholder="mi-oferta-premium"
                    />
                  </div>
                </div>
              </Section>

              {visibility.showPricing ? (
                <Section
                id="pricing"
                icon={DollarSign}
                title="Precio y venta"
                description="Agrega precio, cantidades, opciones de pago y facilidades."
                open={openSection === 'pricing'}
                onToggle={toggleSection}
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Precio</Label>
                      <Input className={fieldClassName}
                        value={form.price}
                        onChange={(e) => updateField(setForm, 'price', e.target.value)}
                        placeholder="29.99"
                        inputMode="decimal"
                      />
                    </div>

                    {visibility.showQuantity ? (
                      <div className="space-y-2">
                        <Label>Cantidad / cupos</Label>
                        <Input className={fieldClassName}
                          value={form.quantity}
                          onChange={(e) =>
                            updateField(setForm, 'quantity', e.target.value)
                          }
                          placeholder="999"
                          inputMode="numeric"
                        />
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <Label>Compra mínima</Label>
                      <Input className={fieldClassName}
                        value={form.minimum}
                        onChange={(e) =>
                          updateField(setForm, 'minimum', e.target.value)
                        }
                        placeholder="1"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-1">
                    {visibility.showRecurring ? (
                        <>
                            <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                                <div>
                                    <div className="font-medium">Cobro recurrente</div>
                                    <p className="text-xs text-muted-foreground">
                                    Activa configuración de membresía o suscripción.
                                    </p>
                                </div>
                                <Switch
                                    checked={form.recurringEnabled}
                                    onCheckedChange={(checked) =>
                                    updateField(setForm, 'recurringEnabled', checked)
                                    }
                                />
                            </div>

                            {form.recurringEnabled ? (
                            <div className="space-y-4">
                                <CommunitySubscriptionsSelector
                                value={form.recurringIds ?? []}
                                onChange={updateRecurringIds}
                                />

                                {form.recurringIds.length === 0 ? (
                                <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                                    Selecciona o crea al menos una suscripción para que esta membresía pueda cobrar de forma recurrente.
                                </p>
                                ) : null}
                            </div>
                            ) : null}
                        </>
                    ) : null}

                    <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                      <div>
                        <div className="font-medium">Meses sin intereses</div>
                        <p className="text-xs text-muted-foreground">
                          Ofrece a tus clientes la posibilidad de pagar en cómodas mensualidades con Stripe — flexible, seguro y sin complicaciones.
                        </p>
                      </div>
                      <Switch
                        checked={form.installments}
                        onCheckedChange={(checked) =>
                          updateField(setForm, 'installments', checked)
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">*Fees desde 5% a 3 meses hasta 10% a 9 meses. Las mensualidades están disponibles solo en determinados países y monedas a través de Stripe.</p>
                  </div>
                </Section>
              ) : null}

              {visibility.showPromo ? (
                <Section
                id="promo"
                icon={Tag}
                title="Promoción"
                description="Agrega precios especiales por tiempo limitado."
                open={openSection === 'promo'}
                onToggle={toggleSection}
                >
                  <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                    <div>
                      <div className="font-medium">Activar promoción</div>
                      <p className="text-xs text-muted-foreground">
                        Precio especial por fecha o por días.
                      </p>
                    </div>
                    <Switch
                      checked={form.promoEnabled}
                      onCheckedChange={(checked) =>
                        updateField(setForm, 'promoEnabled', checked)
                      }
                    />
                  </div>

                  {form.promoEnabled ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Tipo de promoción</Label>
                        <select className={selectClassName}
                          value={form.promoType}
                          onChange={(e) =>
                            updateField(
                              setForm,
                              'promoType',
                              e.target.value as ProductForm['promoType']
                            )
                          }
                        >
                          <option value="date">Por fechas</option>
                          <option value="days">Por días</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label>Precio promocional</Label>
                        <Input className={fieldClassName}
                          value={form.promoPrice}
                          onChange={(e) =>
                            updateField(setForm, 'promoPrice', e.target.value)
                          }
                          placeholder="19.99"
                          inputMode="decimal"
                        />
                      </div>

                      {form.promoType === 'date' ? (
                        <>
                          <div className="space-y-2">
                            <Label>Inicio</Label>
                            <Input className={fieldClassName}
                              type="date"
                              value={form.promoDateStart}
                              onChange={(e) =>
                                updateField(setForm, 'promoDateStart', e.target.value)
                              }
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Fin</Label>
                            <Input className={fieldClassName}
                              type="date"
                              value={form.promoDateEnd}
                              onChange={(e) =>
                                updateField(setForm, 'promoDateEnd', e.target.value)
                              }
                            />
                          </div>
                        </>
                      ) : (
                        <div className="space-y-2 md:col-span-2">
                          <Label>Días activos</Label>
                          <Input
                            className={fieldClassName}
                            value={form.promoDays}
                            onChange={(e) =>
                              updateField(setForm, 'promoDays', e.target.value)
                            }
                            placeholder="3"
                            inputMode="numeric"
                          />

                          <p className="text-xs text-muted-foreground">
                            El precio promocional aplicará durante{' '}
                            <span className="font-semibold text-foreground">
                              {Number(form.promoDays || 0) || 0}
                            </span>{' '}
                            {Number(form.promoDays || 0) === 1 ? 'día' : 'días'} después de que el usuario se registre.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </Section>
              ) : null}

              {visibility.showMedia ? (
                <Section
                id="media"
                icon={ImageIcon}
                title="Imagen y video"
                description="Agrega la imagen principal, imágenes opcionales y video."
                open={openSection === 'media'}
                onToggle={toggleSection}
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-3 md:col-span-2">
                      <Label>Imagen principal</Label>

                      {form.image ? (
                        <div className="group relative overflow-hidden rounded-2xl border border-border bg-muted max-w-md">
                          <img
                            src={form.image}
                            alt="Imagen principal"
                            className="h-56 w-full object-cover"
                          />

                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={openMainImageManager}
                              className="gap-2"
                            >
                              <ImageIcon className="h-4 w-4" />
                              Cambiar imagen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={openMainImageManager}
                          className="flex h-56 max-w-md w-full flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40 text-muted-foreground transition hover:bg-muted/70 hover:text-foreground"
                        >
                          <ImageIcon className="mb-3 h-10 w-10" />
                          <span className="text-sm font-medium">Seleccionar imagen principal</span>
                          <span className="mt-1 text-xs text-muted-foreground">
                            Usa el image manager para elegir o subir una imagen
                          </span>
                        </button>
                      )}
                    </div>

                    <div className="space-y-3 md:col-span-2">
                      <Label>Video promocional</Label>

                      {form.video ? (
                        <div className="group relative max-w-md overflow-hidden rounded-2xl border border-border bg-black">
                          <TrackableVideoEmbed
                            url={form.video}
                            rounded="xl"
                            variant="plain"
                            autoplay={false}
                            muted={false}
                            controls
                          />

                          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => setVideoModalOpen(true)}
                              className="gap-2"
                            >
                              <Video className="h-4 w-4" />
                              Cambiar video
                            </Button>

                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => updateField(setForm, 'video', '')}
                              className="gap-2"
                            >
                              <X className="h-4 w-4" />
                              Quitar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setVideoModalOpen(true)}
                          className="flex h-56 max-w-md w-full flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40 text-muted-foreground transition hover:bg-muted/70 hover:text-foreground"
                        >
                          <Video className="mb-3 h-10 w-10" />
                          <span className="text-sm font-medium">Agregar video promocional</span>
                          <span className="mt-1 text-xs text-muted-foreground">
                            YouTube, Vimeo o URL compatible
                          </span>
                        </button>
                      )}
                    </div>

                    {visibility.showGallery ? (
                      <div className="space-y-3 md:col-span-2">
                        <div className="flex items-center justify-between gap-3">
                          <Label>Galería</Label>

                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openGalleryImageManager(null)}
                            className="gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            Agregar imagen
                          </Button>
                        </div>

                        {form.gallery.length > 0 ? (
                          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                            {form.gallery.map((img, index) => (
                              <div
                                key={`${img.image}-${index}`}
                                className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-muted"
                              >
                                {img.image ? (
                                  <img
                                    src={img.image}
                                    alt={`Galería ${index + 1}`}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openGalleryImageManager(index)}
                                    className="flex h-full w-full flex-col items-center justify-center text-muted-foreground hover:text-foreground"
                                  >
                                    <ImageIcon className="h-6 w-6" />
                                  </button>
                                )}

                                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="secondary"
                                    onClick={() => openGalleryImageManager(index)}
                                    className="h-8 w-8 rounded-full"
                                  >
                                    <ImageIcon className="h-4 w-4" />
                                  </Button>

                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="destructive"
                                    onClick={() => {
                                      const next = form.gallery.filter((_, i) => i !== index)
                                      updateField(setForm, 'gallery', next)
                                    }}
                                    className="h-8 w-8 rounded-full"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() => openGalleryImageManager(null)}
                              className="flex aspect-square flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 text-muted-foreground transition hover:bg-muted/70 hover:text-foreground"
                            >
                              <Plus className="mb-1 h-5 w-5" />
                              <span className="text-xs font-medium">Agregar</span>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openGalleryImageManager(null)}
                            className="flex h-32 w-full flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40 text-muted-foreground transition hover:bg-muted/70 hover:text-foreground"
                          >
                            <ImageIcon className="mb-2 h-7 w-7" />
                            <span className="text-sm font-medium">Agregar imágenes de galería</span>
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                </Section>
              ) : null}

              {visibility.showEventSchedule ? (
                <Section
                id="event"
                icon={CalendarDays}
                title="Fechas del evento / reto"
                description="Replica duration, date_start, datetime_start, date_end y datetime_end."
                open={openSection === 'event'}
                onToggle={toggleSection}
                >
                  <div className="space-y-2">
                    <Label>Duración</Label>
                    <select className={selectClassName}
                      value={form.duration}
                      onChange={(e) =>
                        updateField(
                          setForm,
                          'duration',
                          e.target.value as ProductForm['duration']
                        )
                      }
                    >
                      <option value="limited">Limitado</option>
                      <option value="forever">Siempre disponible</option>
                    </select>
                  </div>

                  {form.duration === 'limited' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Fecha de inicio</Label>
                        <Input className={fieldClassName}
                          type="date"
                          value={form.dateStart}
                          onChange={(e) =>
                            updateField(setForm, 'dateStart', e.target.value)
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Hora de inicio</Label>
                        <Input className={fieldClassName}
                          type="time"
                          value={form.timeStart}
                          onChange={(e) =>
                            updateField(setForm, 'timeStart', e.target.value)
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Fecha de cierre</Label>
                        <Input className={fieldClassName}
                          type="date"
                          value={form.dateEnd}
                          onChange={(e) =>
                            updateField(setForm, 'dateEnd', e.target.value)
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Hora de cierre</Label>
                        <Input className={fieldClassName}
                          type="time"
                          value={form.timeEnd}
                          onChange={(e) =>
                            updateField(setForm, 'timeEnd', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
                      Esta oferta queda disponible sin fecha de cierre.
                    </p>
                  )}

                  <div className="space-y-2">
                    <Label>Ubicación / link</Label>
                    <Input className={fieldClassName}
                      value={form.location}
                      onChange={(e) => updateField(setForm, 'location', e.target.value)}
                      placeholder="Zoom, Google Meet, dirección o texto libre"
                    />
                  </div>
                </Section>
              ) : null}

              {visibility.showAvailability ? (
                <Section
                id="availability"
                icon={CalendarDays}
                title="Disponibilidad y expiración"
                description="Muestra el producto a partir de una fecha y restrige el acceso."
                open={openSection === 'availability'}
                onToggle={toggleSection}
                >
                  {!visibility.showEventSchedule ? (
                    <div className="space-y-2">
                      <Label>Disponible desde</Label>
                      <Input className={fieldClassName}
                        type="date"
                        value={form.dateAvailable}
                        onChange={(e) =>
                          updateField(setForm, 'dateAvailable', e.target.value)
                        }
                      />
                    </div>
                  ) : null}

                  {visibility.showExpiration ? (
                    <>
                      <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                        <div>
                          <div className="font-medium">Expira el acceso</div>
                          <p className="text-xs text-muted-foreground">
                            Define cuánto dura el acceso después de comprar.
                          </p>
                        </div>
                        <Switch
                          checked={form.expiresEnabled}
                          onCheckedChange={(checked) =>
                            updateField(setForm, 'expiresEnabled', checked)
                          }
                        />
                      </div>

                      {form.expiresEnabled ? (
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Expira en</Label>
                            <Input className={fieldClassName}
                              value={form.expiresIn}
                              onChange={(e) =>
                                updateField(setForm, 'expiresIn', e.target.value)
                              }
                              placeholder="3"
                              inputMode="numeric"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Periodo</Label>
                            <select className={selectClassName}
                              value={form.expiresType}
                              onChange={(e) =>
                                updateField(
                                  setForm,
                                  'expiresType',
                                  e.target.value as ProductForm['expiresType']
                                )
                              }
                            >
                              <option value="day">Día(s)</option>
                              <option value="week">Semana(s)</option>
                              <option value="month">Mes(es)</option>
                              <option value="year">Año(s)</option>
                            </select>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </Section>
              ) : null}

              {visibility.showAccess ? (
                <Section
                id="access"
                icon={LockKeyhole}
                title="Contenido incluido"
                description="Aquí reemplazamos categories/downloads/related/access de legacy con IDs por ahora."
                open={openSection === 'access'}
                onToggle={toggleSection}
                >
                  <NumberIdListEditor
                    label="Cursos incluidos"
                    description="IDs separados por coma. Luego lo cambiamos por buscador visual."
                    value={form.accessCourseIds}
                    placeholder="12, 15, 18"
                    onChange={(ids) => updateField(setForm, 'accessCourseIds', ids)}
                  />

                  <NumberIdListEditor
                    label="Retos incluidos"
                    description="IDs separados por coma."
                    value={form.accessChallengeIds}
                    placeholder="45, 46"
                    onChange={(ids) => updateField(setForm, 'accessChallengeIds', ids)}
                  />

                  <NumberIdListEditor
                    label="Eventos incluidos"
                    description="IDs separados por coma."
                    value={form.accessEventIds}
                    placeholder="9, 10"
                    onChange={(ids) => updateField(setForm, 'accessEventIds', ids)}
                  />

                  {(visibility.showDownloads || form.accessDownloadIds.length > 0) ? (
                    <NumberIdListEditor
                      label="Descargables incluidos"
                      description="IDs separados por coma."
                      value={form.accessDownloadIds}
                      placeholder="3, 7"
                      onChange={(ids) => updateField(setForm, 'accessDownloadIds', ids)}
                    />
                  ) : null}
                </Section>
              ) : null}

              <Section
                id="thankyou"
                icon={PartyPopper}
                title="Página de agradecimiento"
                description="Configura lo que verá y podrá hacer el cliente después de completar su compra."
                open={openSection === 'thankyou'}
                onToggle={toggleSection}
              >
                <div className="space-y-5">
                  <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                    <div className="pr-4">
                      <div className="font-medium">
                        Página de agradecimiento personalizada
                      </div>

                      <p className="mt-1 text-xs text-muted-foreground">
                        Muestra un mensaje personalizado y define a dónde llevar al cliente
                        después de su compra.
                      </p>
                    </div>

                    <Switch
                      checked={form.thankyou.status}
                      onCheckedChange={(checked) =>
                        updateThankyouField(setForm, 'status', checked)
                      }
                    />
                  </div>

                  {form.thankyou.status ? (
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label>Título</Label>

                        <Input
                          className={fieldClassName}
                          value={form.thankyou.title}
                          onChange={(e) =>
                            updateThankyouField(setForm, 'title', e.target.value)
                          }
                          placeholder="¡Gracias por tu compra!"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Mensaje</Label>

                        <RichTextEditor
                          value={form.thankyou.message}
                          onChange={(value) =>
                            updateThankyouField(setForm, 'message', value)
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Video de agradecimiento</Label>

                        <Input
                          className={fieldClassName}
                          value={form.thankyou.video}
                          onChange={(e) =>
                            updateThankyouField(setForm, 'video', e.target.value)
                          }
                          placeholder="https://youtube.com/..."
                        />

                        <p className="text-xs text-muted-foreground">
                          Opcional. Puedes agregar un video de YouTube, Vimeo o una URL
                          compatible.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>¿Qué sucede después?</Label>

                        <select
                          className={fieldClassName}
                          value={form.thankyou.action}
                          onChange={(e) =>
                            updateThankyouField(
                              setForm,
                              'action',
                              e.target.value as ThankYouAction
                            )
                          }
                        >
                          <option value="home">Ir al inicio</option>
                          <option value="onboarding">Comenzar onboarding</option>
                          <option value="moments">Ir a Moments</option>
                          <option value="community">Ir a la comunidad</option>
                          <option value="order">Ver mi pedido</option>
                          <option value="membership">Ver mi membresía</option>
                          <option value="link">Abrir un enlace personalizado</option>
                        </select>

                        <p className="text-xs text-muted-foreground">
                          Define el destino del botón que verá el cliente después de comprar.
                        </p>
                      </div>

                      {form.thankyou.action === 'link' ? (
                        <div className="space-y-2">
                          <Label>Enlace personalizado</Label>

                          <Input
                            className={fieldClassName}
                            value={form.thankyou.link}
                            onChange={(e) =>
                              updateThankyouField(setForm, 'link', e.target.value)
                            }
                            placeholder="https://..."
                          />
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <Label>Texto del botón</Label>

                        <Input
                          className={fieldClassName}
                          value={form.thankyou.button}
                          onChange={(e) =>
                            updateThankyouField(setForm, 'button', e.target.value)
                          }
                          placeholder="Continuar"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </Section>

              {visibility.showUpsells ? (
                <Section
                id="upsells"
                icon={Layers}
                title="Upsells"
                description="Replica el tab Upsells. Por ahora queda listo como estructura visual."
                open={openSection === 'upsells'}
                onToggle={toggleSection}
                >
                  {form.upsells.length === 0 ? (
                    <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                      Sin upsells configurados.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {form.upsells.map((upsell, index) => (
                        <div
                          key={`${upsell.productId}-${index}`}
                          className="flex items-center justify-between rounded-xl border p-3"
                        >
                          <div>
                            <div className="font-medium">{upsell.name}</div>
                            <div className="text-xs text-muted-foreground">
                              ID {upsell.productId} · Orden {upsell.sortOrder}
                            </div>
                          </div>
                          <Switch
                            checked={upsell.status}
                            onCheckedChange={(checked) => {
                              const next = [...form.upsells]
                              next[index] = { ...upsell, status: checked }
                              updateField(setForm, 'upsells', next)
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <Button type="button" variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Agregar upsell
                  </Button>
                </Section>
              ) : null}

              {visibility.showAdvanced ? (
                <Section
                id="advanced"
                icon={Settings2}
                title="Avanzado"
                description="SEO y permisos de visualización."
                open={openSection === 'advanced'}
                onToggle={toggleSection}
                >
                  {visibility.showSeo ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Meta title</Label>
                        <Input className={fieldClassName}
                          value={form.metaTitle}
                          onChange={(e) =>
                            updateField(setForm, 'metaTitle', e.target.value)
                          }
                          placeholder="Título SEO"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label>Meta keywords</Label>
                        <textarea className={textareaClassName}
                          value={form.metaKeyword}
                          onChange={(e) =>
                            updateField(setForm, 'metaKeyword', e.target.value)
                          }
                          placeholder="palabras, clave, separadas"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label>Tags</Label>
                        <Input className={fieldClassName}
                          value={form.tag}
                          onChange={(e) => updateField(setForm, 'tag', e.target.value)}
                          placeholder="marketing, ia, comunidad"
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="grid gap-3">
                    <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                      <div>
                        <div className="font-medium">Puede clonarse</div>
                        <p className="text-xs text-muted-foreground">
                          Permite que otros creadores puedan clonar tu producto o enviar tráfico a tu comunidad.
                        </p>
                      </div>
                      <Switch
                        checked={form.canClone}
                        onCheckedChange={(checked) =>
                          updateField(setForm, 'canClone', checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                      <div>
                        <div className="font-medium">Puede promoverse</div>
                        <p className="text-xs text-muted-foreground">
                          Permite a otras comunidades promover tu producto en la sección de ofertas.
                        </p>
                      </div>
                      <Switch
                        checked={form.canPromote}
                        onCheckedChange={(checked) =>
                          updateField(setForm, 'canPromote', checked)
                        }
                      />
                    </div>
                  </div>
                </Section>
              ) : null}

              {affiliatesEnabled ? (
                <Section
                  id="affiliates"
                  icon={Handshake}
                  title="Afiliados"
                  description="Selecciona el plan de comisiones que aplicará a este producto."
                  open={openSection === 'affiliates'}
                  onToggle={toggleSection}
                >
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Plan de afiliados</Label>

                      <select
                        className={selectClassName}
                        value={form.mtaSchemeId || ''}
                        onChange={(e) => {
                          const value = Number(e.target.value || 0)

                          updateField(
                            setForm,
                            'mtaSchemeId',
                            value > 0 ? value : null
                          )
                        }}
                      >
                        <option value="">Sin plan de afiliados</option>

                        {affiliateSchemes.map((scheme) => (
                          <option
                            key={scheme.mta_scheme_id}
                            value={scheme.mta_scheme_id}
                          >
                            {scheme.name}
                          </option>
                        ))}
                      </select>

                      <p className="text-xs text-muted-foreground">
                        Este plan define las comisiones que podrán recibir los afiliados al promover este producto.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-black/5 bg-muted/20 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <Label>Habilitar como membresía del plan de compensación</Label>
                          <p className="text-xs leading-5 text-muted-foreground">
                            Al activar esta opción, este producto dará acceso o renovará la membresía del afiliado dentro del plan de compensación.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            updateField(setForm, 'isMembership', !form.isMembership)
                          }
                          className={cn(
                            'relative h-7 w-12 shrink-0 rounded-full transition-colors',
                            form.isMembership ? 'bg-primary' : 'bg-muted-foreground/25'
                          )}
                        >
                          <span
                            className={cn(
                              'absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all',
                              form.isMembership ? 'left-6' : 'left-1'
                            )}
                          />
                        </button>
                      </div>

                      {form.isMembership ? (
                        <div className="mt-4 grid gap-4 sm:grid-cols-[140px_1fr]">
                          <div className="space-y-2">
                            <Label>Expira en</Label>
                            <Input
                              type="number"
                              min="1"
                              value={form.membershipExpiresIn}
                              onChange={(e) =>
                                updateField(setForm, 'membershipExpiresIn', e.target.value)
                              }
                              className="rounded-2xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Tipo de expiración</Label>
                            <select
                              className={selectClassName}
                              value={form.membershipExpiresType}
                              onChange={(e) =>
                                updateField(
                                  setForm,
                                  'membershipExpiresType',
                                  e.target.value as ProductForm['membershipExpiresType']
                                )
                              }
                            >
                              <option value="day">Día(s)</option>
                              <option value="week">Semana(s)</option>
                              <option value="month">Mes(es)</option>
                              <option value="year">Año(s)</option>
                            </select>
                          </div>

                          <p className="sm:col-span-2 text-xs text-muted-foreground">
                            Ejemplo: <span className="font-medium text-foreground">1 año</span> renovará la membresía del afiliado por un año al comprar este producto.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Section>
              ) : null}

                
                <div className="px-6 py-6">
                    <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                      <div>
                        <div className="font-medium">Producto activo</div>
                        <p className="text-xs text-muted-foreground">
                          Si está apagado, queda como borrador.
                        </p>
                      </div>
                      <Switch
                        checked={form.status}
                        onCheckedChange={(checked) =>
                          updateField(setForm, 'status', checked)
                        }
                      />
                    </div>
                </div>
                <div className="px-6 pb-6">
                    <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0f172a]">
                    <div>
                        <div className="font-medium">Visible públicamente</div>
                        <p className="text-xs text-muted-foreground">
                        Puedes mostrar u ocultar tu oferta en la pestaña de ofertas de tu comunidad.
                        </p>
                    </div>
                    <Switch
                        checked={form.isPublished}
                        onCheckedChange={(checked) =>
                        updateField(setForm, 'isPublished', checked)
                        }
                    />
                    </div>
                </div>
            </div>
          </div>
        )}

        <div className="shrink-0 border-t border-border bg-background/95 px-6 py-4 backdrop-blur">
            <div className="flex w-full items-center justify-between gap-3">
                <div className="hidden items-center gap-2 text-sm text-muted-foreground sm:flex">
                {form.isPublished ? (
                    <>
                    <Eye className="h-4 w-4" />
                    Publicado
                    </>
                ) : (
                    <>
                    <Package className="h-4 w-4" />
                    Borrador
                    </>
                )}
                </div>

                <div className="ml-auto flex gap-2">
                <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={saving}
                    className="h-11 rounded-xl border-black/10 bg-white px-5 shadow-sm dark:border-white/10 dark:bg-[#0f172a]"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={onSave}
                    disabled={saving || loading || !form.name.trim()}
                    className="h-11 rounded-xl px-5 shadow-sm"
                >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Guardando...' : 'Guardar producto'}
                </Button>
                </div>
            </div>
        </div>
      </SheetContent>
    </Sheet>
    <ImageManager
      open={imageManagerOpen}
      onClose={() => {
        setImageManagerOpen(false)
        setGalleryReplaceIndex(null)
      }}
      onSelect={handleSelectImage}
      sellerId={sellerId}
    />
    <VideoUrlModal
      open={videoModalOpen}
      initialUrl={form.video}
      onClose={() => setVideoModalOpen(false)}
      onSubmit={(url) => {
        updateField(setForm, 'video', url)
        setVideoModalOpen(false)
      }}
    />
    </>
  )
}